import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Grid, FormControlLabel, Checkbox } from '@mui/material';
import { Autocomplete, TextField } from '@totalsoft/rocket-ui';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { numericPropertyType } from 'constants/propertyType';
import { ADMIN_PROPERTIES_WRITE } from 'constants/rightsCodes';
import { usePermission } from 'providers/RightsHooks';

export const PropertyGeneralDataComponent = ({
  property,
  propertyTypes,
  propertyCategories,
  contexts,
  applications,
  onChange,
  onLookupPropertyChanged,
  onApplicationsChanged,
  validation
}) => {
  const { t } = useTranslation();
  const canWrite = usePermission(ADMIN_PROPERTIES_WRITE);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} lg={4}>
        <TextField
          fullWidth
          value={property.propertyCode || ''}
          label={t('Property.PropertyCode')}
          onChange={event => onChange('propertyCode', event)}
          error={!isValid(validation.propertyCode)}
          helperText={getErrors(validation.propertyCode)}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={4}>
        <TextField
          fullWidth
          value={property.propertyName || ''}
          label={t('Property.PropertyName')}
          onChange={event => onChange('propertyName', event)}
          error={!isValid(validation.propertyName)}
          helperText={getErrors(validation.propertyName)}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={4}>
        <TextField
          isNumeric
          fullWidth
          value={property.orderNo || ''}
          label={t('Property.OrderNo')}
          decimalScale={0}
          readOnly={true}
          onChange={event => {
            if (event !== property.orderNo) onChange('orderNo', event);
          }}
          disabled
          error={!isValid(validation.orderNo)}
          helperText={getErrors(validation.orderNo)}
        />
      </Grid>
      <Grid item xs={12} sm={6} lg={4}>
        <Autocomplete
          fullWidth
          value={property.propertyTypeId}
          simpleValue
          valueKey='dictionaryItemId'
          labelKey='dictionaryItemName'
          options={propertyTypes}
          label={t('Property.PropertyType')}
          onChange={event => onChange('propertyTypeId', event)}
          error={!isValid(validation.propertyTypeId)}
          helperText={getErrors(validation.propertyTypeId)}
          disabled={!canWrite}
        />
      </Grid>
      {property.propertyTypeId === numericPropertyType && (
        <Grid item xs={12} sm={6} lg={4}>
          <TextField
            isNumeric
            fullWidth
            value={property.decimalPlaces || ''}
            label={t('Property.DecimalPlaces')}
            decimalScale={0}
            onChange={event => onChange('decimalPlaces', event)}
            error={!isValid(validation.decimalPlaces)}
            helperText={getErrors(validation.decimalPlaces)}
            disabled={!canWrite}
          />
        </Grid>
      )}
      <Grid item xs={12} sm={6} md={4}>
        <Autocomplete
          fullWidth
          value={property.categoryId}
          simpleValue
          valueKey='dictionaryItemId'
          labelKey='dictionaryItemName'
          options={propertyCategories}
          label={t('Property.Category')}
          onChange={event => onChange('categoryId', event)}
          isSearchable={true}
          disabled={!canWrite}
        />
      </Grid>
      <Grid item container xs={12}>
        <Grid item xs={12} md={6} lg={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={property.readOnly || false}
                onChange={event => onChange('readOnly', event.target.checked)}
                color='primary'
              />
            }
            label={t('Property.ReadOnly')}
            disabled={!canWrite}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={property.allowDefaultRead || false}
                onChange={event => onChange('allowDefaultRead', event.target.checked)}
                color='primary'
              />
            }
            label={t('Property.AllowDefaultRead')}
            disabled={!canWrite}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={2}>
          <FormControlLabel
            control={
              <Checkbox
                checked={property.allowDefaultWrite || false}
                onChange={event => onChange('allowDefaultWrite', event.target.checked)}
                color='primary'
              />
            }
            label={t('Property.AllowDefaultWrite')}
            disabled={!canWrite}
          />
        </Grid>
        <Grid item xs={12} md={6} lg={4}>
          <FormControlLabel
            control={
              <Checkbox
                checked={property.allowAllContext || false}
                onChange={event => onChange('allowAllContext', event.target.checked)}
                color='primary'
              />
            }
            label={t('Property.AllowAllContext')}
            disabled={!canWrite}
          />
        </Grid>
      </Grid>
      <Grid item xs={12} md={6}>
        <Autocomplete
          fullWidth
          value={property.propertyApplications}
          valueKey='appCode'
          labelKey='appName'
          isMultiSelection={true}
          options={applications}
          label={t('Property.Applications')}
          onChange={onApplicationsChanged}
          isSearchable={true}
          isClearable={true}
          disabled={!canWrite}
        />
      </Grid>
      {!property.allowAllContext && (
        <Grid item xs={12} md={6}>
          <Autocomplete
            fullWidth={false}
            label={t('Property.Context')}
            value={property.propertyContexts?.map(item => {
              return item?.contextId;
            })}
            options={contexts}
            onChange={onLookupPropertyChanged}
            labelKey={'dictionaryItemName'}
            valueKey={'dictionaryItemId'}
            isMultiSelection={true}
            simpleValue={true}
            isClearable={true}
            isSearchable={true}
            disabled={!canWrite}
          />
        </Grid>
      )}
    </Grid>
  );
};

PropertyGeneralDataComponent.propTypes = {
  property: PropTypes.object.isRequired,
  propertyTypes: PropTypes.array.isRequired,
  propertyCategories: PropTypes.array.isRequired,
  contexts: PropTypes.array.isRequired,
  applications: PropTypes.array,
  onChange: PropTypes.func.isRequired,
  onLookupPropertyChanged: PropTypes.func.isRequired,
  onApplicationsChanged: PropTypes.func,
  validation: PropTypes.object.isRequired
};

export default PropertyGeneralDataComponent;
